import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"
import { defaultAssistantName } from "../utils/brand"
import { CutoutPlayer } from "./jane_cutout_player"
import { portraitAppearance } from "../utils/avatar_appearance"
import { plainText } from "../utils/plain_text"

// The floating Jane avatar (shared/_jane_avatar): drag it anywhere, click to
// open the Jane sidebar, and watch it think and talk while a reply is being
// written — whichever device asked (AssistantActivityChannel carries the
// reply's lifecycle from the server).
//
// Position is remembered per browser as distances from the bottom-right
// corner, so the default spot and a window resize both behave naturally.
// Size and "show as a header button instead" are account preferences.
const POSITION_KEY = "jane_avatar_position"
const DRAG_THRESHOLD = 4
const EDGE = 8
const BUBBLE_MS = 10000
// Controls the avatar must never sit on: it lifts itself above them. Pages
// can mark their own with data-jane-avatar-avoid.
const AVOID = [
  ".composer-card",
  ".assistant-input",
  ".msg-detail-compose",
  ".contact-tree-minimap",
  ".contact-tree-minimap-toggle",
  "[data-jane-avatar-avoid]"
].join(", ")
const AVOID_EVERY_MS = 1500
// How often a tab in front re-announces itself, so Jane for Mac knows it is
// still there (AvatarHandoff forgets a tab it hasn't heard from in 45s).
const ANNOUNCE_EVERY_MS = 20000
// The people beside Jane: three faces at most (Jane::People), refreshed this often.
const PEOPLE_SHOWN = 3
const PEOPLE_EVERY_MS = 60000

// Which clip plays for each mood, first available wins.
// A huddle stays on screen this long after its last line, and closes on its
// own if it never hears "done".
const HUDDLE_LINGER_MS = 9000
const HUDDLE_MAX_MS = 90000
// Too close to the left edge for the twin to step out on the left.
const HUDDLE_ROOM = 280
// Aloud or not is remembered in the browser.
const HUDDLE_VOICE_KEY = "jane_huddle_voice"
// A line with no audio (voice off, or it failed) stays up this long before
// the next one takes the floor.
const HUDDLE_READ_MS = 1800
// The conversation strip keeps this many lines on screen.
const HUDDLE_LINES = 3
// A huddle event that arrives early waits this long for the ones before it
// (Action Cable doesn't keep a stream's order) before the gap is skipped.
const HUDDLE_GAP_MS = 1500

const MOOD_CLIPS = {
  idle: ["rest", "rest", "rest_2", "rest_3", "idle"],
  thinking: ["thinking"],
  talking: ["talking", "talking_2", "talking_3"],
  listening: ["listening", "listening_2"]
}

export default class extends Controller {
  static targets = ["face", "video", "bubble", "bubbleText", "menu", "people", "huddle", "huddleLines", "twin", "twinName", "twinVideo", "huddleVoice", "talk", "talkTopic", "menuMain", "menuSettings"]
  static values = {
    appearance: String, size: String, prefsUrl: String, chatUrl: String, clips: Object, cutouts: Object, peopleUrl: String,
    // Who is in the corner (UserSetting#jane_avatar_persona): Jane, or you —
    // your twin. The companion is whoever isn't: first in the stack, and the
    // one who steps out beside the corner in a huddle.
    twinShown: Boolean, personaId: Number, companion: Object, meThreadUrl: String
  }

  connect() {
    this._position = this._loadPosition()
    this._placed = this._hasSavedPosition()
    this._applyPosition()
    this._reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches

    this._onAppearance = event => {
      if (!["character", "portrait"].includes(event.detail?.appearance)) return
      this.appearanceValue = event.detail.appearance
      this._syncAppearance()
    }
    window.addEventListener("jane:appearance", this._onAppearance)
    this._onResize = () => { this._syncAppearance(); this._applyPosition(); this._dodgeSidebar(); this._fitMenu() }
    this._onSidebarChange = () => setTimeout(() => this._dodgeSidebar(), 260)
    // Composers and panels render after the page does; keep checking. A
    // sidebar opening or closing (by its own buttons too) is caught at once.
    this._avoidTimer = setInterval(() => this._dodgeSidebar(), AVOID_EVERY_MS)
    if (window.ResizeObserver) {
      this._panelObserver = new ResizeObserver(() => this._dodgeSidebar())
      this._observePanels()
      this._onPanelsMaybeReplaced = () => this._observePanels()
      document.addEventListener("turbo:load", this._onPanelsMaybeReplaced)
    }
    this._onVoice = (event) => this._voice(event.detail?.state)
    window.addEventListener("resize", this._onResize)
    window.addEventListener("jane:voice", this._onVoice)
    document.addEventListener("jane-sidebar:toggle", this._onSidebarChange)
    document.addEventListener("jane-sidebar:restore", this._onSidebarChange)
    document.addEventListener("turbo:load", this._onSidebarChange)
    window.addEventListener("jane:panels-changed", this._onSidebarChange)

    this._onClickCapture = (event) => this._swallowDragClick(event)
    this.element.addEventListener("click", this._onClickCapture, true)

    // One Jane at a time: while this tab is in front, Jane for Mac steps aside.
    this._clientId = (crypto.randomUUID && crypto.randomUUID()) || String(Math.random()).slice(2)
    this._onFront = () => this._announce()
    this._onLeave = () => this._announce(false)
    window.addEventListener("focus", this._onFront)
    window.addEventListener("blur", this._onFront)
    document.addEventListener("visibilitychange", this._onFront)
    window.addEventListener("pagehide", this._onLeave)
    this._announceTimer = setInterval(() => { if (this._inFront()) this._announce() }, ANNOUNCE_EVERY_MS)

    this._onPeopleChanged = () => this._loadPeople()
    window.addEventListener("jane:people-changed", this._onPeopleChanged)
    window.addEventListener("focus", this._onPeopleChanged)
    this._peopleTimer = setInterval(() => { if (!document.hidden) this._loadPeople() }, PEOPLE_EVERY_MS)
    this._loadPeople()

    this._portrait = null
    this._syncAppearance()
    this._onVisibility = () => {
      if (document.visibilityState === "visible") {
        this._figure?.resume()
        this._twinFigure?.resume()
      }
    }
    document.addEventListener("visibilitychange", this._onVisibility)

    this._setMood("idle")
    this._subscribe()
    this._dodgeSidebar()
  }

  // A persona with cut-out clips stands on the page as a character (the
  // Mac's look) instead of sitting in a circle. Returns the player, or null
  // where there are none or WebGL isn't available.
  _standUp(face, host, cutouts) {
    if (this._portrait || !cutouts?.clips || !Object.keys(cutouts.clips).length || !CutoutPlayer.supported() || this._reducedMotion) return null
    try {
      // She is data-turbo-permanent, so every visit disconnects and
      // reconnects this controller on the SAME element: a canvas left from
      // the last connect still holds its last frame, and drew a second,
      // frozen Jane behind the live one. Never more than one.
      face.querySelectorAll(".jane-avatar-canvas").forEach((old) => old.remove())
      const canvas = document.createElement("canvas")
      canvas.className = "jane-avatar-canvas"
      canvas.setAttribute("aria-hidden", "true")
      face.prepend(canvas)
      const player = new CutoutPlayer(canvas)
      player.clips = cutouts.clips
      host.classList.add(host === this.element ? "jane-avatar--figure" : "jane-avatar-twin--figure")
      host.style.setProperty("--jane-figure-aspect", String(cutouts.aspect || 0.6))
      return player
    } catch (_error) {
      return null
    }
  }

  disconnect() {
    window.removeEventListener("jane:appearance", this._onAppearance)
    clearTimeout(this._unhideTimer)
    window.removeEventListener("resize", this._onResize)
    window.removeEventListener("jane:voice", this._onVoice)
    document.removeEventListener("jane-sidebar:toggle", this._onSidebarChange)
    document.removeEventListener("jane-sidebar:restore", this._onSidebarChange)
    document.removeEventListener("turbo:load", this._onSidebarChange)
    window.removeEventListener("jane:panels-changed", this._onSidebarChange)
    this.element.removeEventListener("click", this._onClickCapture, true)
    clearTimeout(this._bubbleTimer)
    clearTimeout(this._gapTimer)
    clearInterval(this._avoidTimer)
    this._panelObserver?.disconnect()
    if (this._onPanelsMaybeReplaced) document.removeEventListener("turbo:load", this._onPanelsMaybeReplaced)
    clearInterval(this._announceTimer)
    clearInterval(this._peopleTimer)
    document.removeEventListener("visibilitychange", this._onVisibility)
    this._figure?.destroy()
    this._figure = null
    this.element.querySelectorAll(".jane-avatar-canvas").forEach((canvas) => canvas.remove())
    this._twinFigure?.destroy()
    window.removeEventListener("jane:people-changed", this._onPeopleChanged)
    window.removeEventListener("focus", this._onPeopleChanged)
    window.removeEventListener("focus", this._onFront)
    window.removeEventListener("blur", this._onFront)
    document.removeEventListener("visibilitychange", this._onFront)
    window.removeEventListener("pagehide", this._onLeave)
    this._announce(false)
    this.closeMenu()
    try {
      this._subscription?.unsubscribe()
      this._consumer?.disconnect()
    } catch (_error) {
      // ignore
    }
  }

  // --- dragging ---------------------------------------------------------

  // The face and the whole pill drag, buttons included (as on GitHub's
  // avatar): a press that moves drags, one that doesn't is a click.
  press(event) {
    if (event.button !== 0) return
    // Start from where she is ON SCREEN — stepped aside or lifted included —
    // so the grab never jumps.
    const dodge = parseFloat(this.element.style.getPropertyValue("--jane-avatar-dodge")) || 0
    const lift = parseFloat(this.element.style.getPropertyValue("--jane-avatar-lift")) || 0
    const start = { x: event.clientX, y: event.clientY, right: this._position.right - dodge, bottom: this._position.bottom - lift }
    this._dragged = false
    // Tracked on the window, not by capturing the pointer: capture would
    // retarget the click that follows onto the handle, and a button's own
    // click (chat, call, ⋯) must still reach the button.
    const move = (e) => {
      const dx = e.clientX - start.x
      const dy = e.clientY - start.y
      if (!this._dragged && Math.hypot(dx, dy) < DRAG_THRESHOLD) return
      if (!this._dragged) {
        // Placed by hand now: from here on she goes anywhere, over sidebars too.
        this._placed = true
        this.element.style.setProperty("--jane-avatar-dodge", "0px")
        this.element.style.setProperty("--jane-avatar-lift", "0px")
      }
      this._dragged = true
      this.element.classList.add("is-dragging")
      document.documentElement.classList.add("jane-avatar-dragging")
      this._position = { right: start.right - dx, bottom: start.bottom - dy }
      this._applyPosition()
    }
    const up = () => {
      window.removeEventListener("pointermove", move)
      window.removeEventListener("pointerup", up)
      window.removeEventListener("pointercancel", up)
      this.element.classList.remove("is-dragging")
      document.documentElement.classList.remove("jane-avatar-dragging")
      if (this._dragged) {
        this._savePosition()
        this._dodgeSidebar()
      }
    }
    window.addEventListener("pointermove", move)
    window.addEventListener("pointerup", up)
    window.addEventListener("pointercancel", up)
  }

  faceClick() {
    this.openChat()
  }

  // The click that ends a drag is not a click on Jane, or on a button.
  _swallowDragClick(event) {
    if (!this._dragged) return
    this._dragged = false
    event.preventDefault()
    event.stopPropagation()
  }

  resetPosition() {
    this.closeMenu()
    this._placed = false
    try { localStorage.removeItem(POSITION_KEY) } catch (_error) { /* private mode */ }
    this._position = this._defaultPosition()
    this._applyPosition()
    this._dodgeSidebar()
  }

  _defaultPosition() {
    return { right: 24, bottom: 24 }
  }

  _hasSavedPosition() {
    try { return Boolean(localStorage.getItem(POSITION_KEY)) } catch (_error) { return false }
  }

  _loadPosition() {
    try {
      const saved = JSON.parse(localStorage.getItem(POSITION_KEY) || "null")
      if (saved && Number.isFinite(saved.right) && Number.isFinite(saved.bottom)) return saved
    } catch (_error) {
      // fall through to the default
    }
    return this._defaultPosition()
  }

  _savePosition() {
    try { localStorage.setItem(POSITION_KEY, JSON.stringify(this._position)) } catch (_error) { /* private mode */ }
  }

  // Kept fully on screen, whatever the window did since it was saved.
  _applyPosition() {
    const width = this.element.offsetWidth || 80
    const height = this.element.offsetHeight || 100
    const right = Math.min(Math.max(this._position.right, EDGE), Math.max(EDGE, window.innerWidth - width - EDGE))
    const bottom = Math.min(Math.max(this._position.bottom, EDGE), Math.max(EDGE, window.innerHeight - height - EDGE))
    this._position = { right, bottom }
    this.element.style.right = `${right}px`
    this.element.style.bottom = `${bottom}px`
  }

  // At her resting spot, when the Jane sidebar or Messages opens over her she
  // steps to its left, and she lifts herself above any control she would
  // cover (a composer's Send button, a map's controls).
  // Placed by hand she keeps her spot, and only steps left while a panel docked at the right edge would
  // cover her (she never covers a panel's text); she does not hunt for controls to lift above.
  _dodgeSidebar() {
    // The Jane sidebar, the Messages drawer, the note/document panel, the detail panel (an email, a job, a
    // Feed list) and the task pane all open on the right.
    const panels = [this._sidebarElement(), document.querySelector(".detail-sidebar--message"), document.querySelector(".detail-sidebar:has(.task-pane)"), document.querySelector(".messages-drawer:not(.hidden) .messages-drawer-card"), document.querySelector(".note-sidebar:not(.hidden) .note-sidebar-card"), document.querySelector(".needs-you-pane.is-open")]
    let shift = 0
    panels.filter((panel) => panel && this._visible(panel)).forEach((panel) => {
      const rect = panel.getBoundingClientRect()
      // Only a panel against the right edge (sidebar, drawer): a floating
      // window can be anywhere, and the avatar just stays where it is.
      if (rect.right < window.innerWidth - 24) return
      const left = rect.left
      const faceRight = window.innerWidth - this._position.right
      if (faceRight > left - EDGE) shift = Math.max(shift, faceRight - (left - 2 * EDGE))
    })
    this.element.style.setProperty("--jane-avatar-dodge", `${-shift}px`)
    this.element.style.setProperty("--jane-avatar-lift", this._placed ? "0px" : `${-this._liftFor(shift)}px`)
  }

  // How far up to go so the avatar, at its resting spot shifted by `shift`,
  // covers none of the AVOID controls.
  _liftFor(shift) {
    const width = this.element.offsetWidth
    const height = this.element.offsetHeight
    const right = window.innerWidth - this._position.right - shift
    const box = { left: right - width, right, bottom: window.innerHeight - this._position.bottom, top: 0 }
    box.top = box.bottom - height
    let lift = 0
    for (let pass = 0; pass < 3; pass++) {
      let moved = false
      document.querySelectorAll(AVOID).forEach((el) => {
        if (this.element.contains(el) || !this._visible(el)) return
        const r = el.getBoundingClientRect()
        const top = box.top - lift
        const bottom = box.bottom - lift
        if (r.right <= box.left || r.left >= box.right || r.bottom <= top || r.top >= bottom) return
        lift += bottom - r.top + EDGE
        moved = true
      })
      if (!moved) break
    }
    return Math.min(lift, Math.max(0, box.top - EDGE))
  }

  // --- opening Jane -----------------------------------------------------

  // Her chat — or, with you in the corner, your twin's.
  openChat() {
    if (this.twinShownValue) {
      this._hideBubble()
      this.openTwin()
      return
    }
    this._openJane()
  }

  async _openJane() {
    this._hideBubble()
    const sidebar = this._sidebarElement()
    const targetId = this.twinShownValue ? this.companionValue.persona_id : this.personaIdValue
    if (!sidebar || Number(sidebar.dataset.janeSidebarPersonaIdValue || 0) !== Number(targetId || 0) || sidebar.dataset.janeSidebarPurposeValue !== "chat") {
      const url = this.twinShownValue ? this.companionValue.thread_url : this.chatUrlValue
      try {
        const response = await fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
        if (!response.ok) throw new Error("Thread unavailable")
        const thread = await response.json()
        window.dispatchEvent(new CustomEvent("jane:compose", { detail: { url: thread.thread_url, title: thread.assistant_name, avatar: thread.assistant_avatar } }))
      } catch (_error) { this._showBubble("Couldn't open that conversation. Try again.") }
      return
    }
    // Messages docked as the right sidebar: Jane takes the slot back (one
    // right sidebar at a time).
    if (sidebar && document.documentElement.classList.contains("messages-docked")) {
      window.dispatchEvent(new CustomEvent("jane:messages-close"))
      requestAnimationFrame(() => {
        if (!this._visible(sidebar)) document.dispatchEvent(new CustomEvent("jane-sidebar:toggle"))
        this._dodgeSidebar()
      })
      return
    }
    if (sidebar) {
      document.dispatchEvent(new CustomEvent("jane-sidebar:toggle"))
      return
    }
    if (window.Turbo) window.Turbo.visit(this.chatUrlValue)
    else window.location.href = this.chatUrlValue
  }

  async call() {
    // Resolve the avatar's persona, never whichever unrelated thread happens
    // to be in the sidebar. A failed lookup must not silently call Jane.
    try {
      const response = await fetch(this.chatUrlValue, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      if (!response.ok) throw new Error("Thread unavailable")
      const thread = await response.json()
      if (!thread.conversation_id) throw new Error("Thread unavailable")
      window.dispatchEvent(new CustomEvent("assistant-voice:open", { detail: {
        conversationId: String(thread.conversation_id), name: thread.assistant_name, avatar: thread.assistant_avatar
      } }))
    } catch (_error) { this._showBubble("Couldn't start this call. Try again.") }
  }

  // "Show" in the options: Jane or you. The same slot as Jane for Mac's, so
  // the choice is saved, and the page redrawn around whoever is there now.
  showPersona(event) {
    const id = event.currentTarget.dataset.personaId
    this.closeMenu()
    if (!id || Number(id) === this.personaIdValue) return
    this._savePrefs({ persona_id: id }).then(() => window.location.reload()).catch(() => {})
  }

  _observePanels() {
    if (!this._panelObserver) return
    this._panelObserver.disconnect()
    ;[this._sidebarElement(), document.querySelector(".messages-drawer"), document.querySelector(".needs-you-pane")].forEach((panel) => {
      if (panel) this._panelObserver.observe(panel)
    })
  }

  _sidebarElement() {
    return document.querySelector("[data-controller~='jane-sidebar']")
  }

  _visible(element) {
    return element.offsetWidth > 0 && element.offsetHeight > 0 && !element.classList.contains("d-none")
  }

  // --- the people beside Jane (Jane::People) ------------------------------

  async _loadPeople() {
    if (!this.hasPeopleTarget || !this.peopleUrlValue) return
    try {
      const response = await fetch(this.peopleUrlValue, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      if (!response.ok) return
      const data = await response.json()
      this._renderPeople(data.people || [], data.me || null)
    } catch (_error) {
      // Keep whoever is shown.
    }
  }

  // At most three faces; the rest fold into "+N", which opens Messages, as
  // does the chat bubble at the end of the stack.
  _renderPeople(people, me = null) {
    // Same people as shown: leave the faces alone (no flicker, and a click
    // in flight still lands on the face it was aimed at).
    this._me = me
    const signature = JSON.stringify([people, me])
    if (signature === this._peopleSignature) return
    this._peopleSignature = signature
    const stack = this.peopleTarget
    stack.replaceChildren()
    // Whoever isn't in the corner comes first: your twin beside Jane, or
    // Jane beside you.
    const companion = this.twinShownValue ? this.companionValue : null
    if (companion?.name) {
      const face = this._personFace({ ...companion, unread: 0 })
      face.classList.add("jane-person--me")
      face.title = companion.name
      face.setAttribute("aria-label", `Open ${companion.name}`)
      const clone = face.cloneNode(true)
      clone.addEventListener("click", () => this._openJane())
      stack.appendChild(clone)
    } else if (me) {
      const face = this._personFace({ ...me, unread: 0 })
      face.classList.add("jane-person--me")
      face.title = `${me.name} (you)`
      face.setAttribute("aria-label", `Talk to yourself: ${me.name}`)
      stack.appendChild(face)
    }
    people.slice(0, PEOPLE_SHOWN).forEach((person) => stack.appendChild(this._personFace(person)))
    const more = people.length - PEOPLE_SHOWN
    if (more > 0) stack.appendChild(this._stackButton(`+${more}`, `${more} more`, () => this._openMessages()))
    stack.appendChild(this._stackButton(null, "All messages", () => this._openMessages(), "bi-chat-dots"))
    this.element.classList.toggle("has-people", people.length > 0 || Boolean(me) || Boolean(companion?.name))
    requestAnimationFrame(() => this._applyPosition())
  }

  _personFace(person) {
    const face = document.createElement("button")
    face.type = "button"
    face.className = "jane-person"
    face.title = person.unread ? `${person.name} · ${person.unread} unread` : person.name
    face.setAttribute("aria-label", face.title)
    if (person.avatar) {
      const img = document.createElement("img")
      img.src = person.avatar
      img.alt = ""
      img.referrerPolicy = "no-referrer"
      img.draggable = false
      img.onerror = () => { img.remove(); face.prepend(this._initials(person.name)) }
      face.appendChild(img)
    } else {
      face.appendChild(this._initials(person.name))
    }
    if (person.unread) {
      const badge = document.createElement("span")
      badge.className = "jane-person-badge"
      badge.textContent = person.unread > 9 ? "9+" : String(person.unread)
      face.appendChild(badge)
    }
    face.addEventListener("click", () => {
      window.dispatchEvent(new CustomEvent("jane:compose", { detail: { url: person.thread_url, title: person.name, avatar: person.avatar } }))
      // Opening the thread reads it; the face goes if it was only there for that.
      setTimeout(() => this._loadPeople(), 4000)
    })
    return face
  }

  _initials(name) {
    const span = document.createElement("span")
    span.className = "jane-person-initials"
    span.textContent = (name || "?").split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase()
    return span
  }

  _stackButton(text, label, onClick, icon = null) {
    const button = document.createElement("button")
    button.type = "button"
    button.className = "jane-person jane-person--more"
    button.title = label
    button.setAttribute("aria-label", label)
    if (icon) {
      const i = document.createElement("i")
      i.className = `bi ${icon}`
      i.setAttribute("aria-hidden", "true")
      button.appendChild(i)
    } else {
      button.textContent = text
    }
    button.addEventListener("click", onClick)
    return button
  }

  _openMessages() {
    window.dispatchEvent(new CustomEvent("jane:messages"))
  }

  // --- the right-click menu ---------------------------------------------

  openMenu(event) {
    event.preventDefault()
    if (!this.hasMenuTarget) return
    this.menuTarget.classList.add("is-open")
    this._fitMenu()
    this.menuTarget.querySelector("button")?.focus()
    this._onOutside = (e) => { if (!this.menuTarget.contains(e.target)) this.closeMenu() }
    setTimeout(() => document.addEventListener("pointerdown", this._onOutside), 0)
  }

  // Settings is also reachable directly from the avatar bar.
  openSettings(event) {
    event?.stopPropagation()
    if (!this.menuTarget.classList.contains("is-open")) this.openMenu(event)
    this._showMenuView("settings")
  }

  closeSettings(event) {
    event?.stopPropagation()
    this._showMenuView("main")
  }

  _showMenuView(view) {
    if (!this.hasMenuMainTarget || !this.hasMenuSettingsTarget) return
    this.menuMainTarget.hidden = view !== "main"
    this.menuSettingsTarget.hidden = view !== "settings"
    const shown = view === "main" ? this.menuMainTarget : this.menuSettingsTarget
    this._fitMenu()
    shown.querySelector("button")?.focus()
  }

  _fitMenu() {
    if (!this.hasMenuTarget || !this.menuTarget.classList.contains("is-open")) return
    this.menuTarget.style.transform = ""
    const host = this.element.getBoundingClientRect()
    const above = Math.max(0, host.top - 18)
    const below = Math.max(0, window.innerHeight - host.bottom - 18)
    const down = below > above
    this.menuTarget.style.top = down ? "calc(100% + 6px)" : "auto"
    this.menuTarget.style.bottom = down ? "auto" : "calc(100% + 6px)"
    this.menuTarget.style.maxHeight = `${Math.max(60, down ? below : above)}px`
    const rect = this.menuTarget.getBoundingClientRect()
    const shift = rect.left < 12 ? 12 - rect.left : Math.min(0, window.innerWidth - 12 - rect.right)
    this.menuTarget.style.transform = `translateX(${shift}px)`
  }

  closeMenu() {
    if (!this.hasMenuTarget) return
    this.menuTarget.classList.remove("is-open")
    if (this.hasTalkTarget) this.talkTarget.hidden = true
    if (this.hasMenuMainTarget) this.menuMainTarget.hidden = false
    if (this.hasMenuSettingsTarget) this.menuSettingsTarget.hidden = true
    if (this._onOutside) document.removeEventListener("pointerdown", this._onOutside)
    this._onOutside = null
  }

  // --- preferences -------------------------------------------------------

  changeAppearance(event) {
    const appearance = event.currentTarget.dataset.appearance
    if (!["character", "portrait"].includes(appearance)) return
    this.appearanceValue = appearance
    this._syncAppearance()
    this.closeMenu()
    this._savePrefs({ appearance })
    window.dispatchEvent(new CustomEvent("jane:appearance", { detail: { appearance } }))
  }

  _syncAppearance() {
    const portrait = portraitAppearance(this.appearanceValue)
    this.element.querySelectorAll("[data-appearance]").forEach(item => {
      const on = item.dataset.appearance === (portrait ? "portrait" : "character")
      item.setAttribute("aria-checked", String(on))
      item.querySelector("i").className = `bi ${on ? "bi-check2" : "bi-dot"}`
    })
    if (portrait === this._portrait) return
    this._portrait = portrait
    this._figure?.destroy()
    this._twinFigure?.destroy()
    this._figure = this._twinFigure = null
    this.element.classList.remove("jane-avatar--figure")
    this.element.classList.toggle("jane-avatar--portrait", portrait)
    this.huddleTarget.classList.remove("jane-avatar-twin--figure")
    this.element.querySelectorAll(".jane-avatar-canvas").forEach(canvas => canvas.remove())
    this.element.querySelectorAll("video").forEach(video => {
      video.pause()
      video.onplaying = null
      video.removeAttribute("src")
      video.load()
      video.hidden = true
    })
    this._clip = this._twinClip = null
    this._figure = this._standUp(this.faceTarget, this.element, this.cutoutsValue)
    if (this._huddleId) {
      this._twinFigure = this._standUp(this.twinTarget, this.huddleTarget, this._companion()?.cutouts)
      this._twinMood("idle")
    }
    this._setMood("idle")
    requestAnimationFrame(() => { this._applyPosition(); this._dodgeSidebar() })
  }

  resize(event) {
    this.closeMenu()
    const size = event.currentTarget.dataset.size
    if (!size || size === this.sizeValue) return
    this.element.classList.remove(`jane-avatar--${this.sizeValue}`)
    this.element.classList.add(`jane-avatar--${size}`)
    this.sizeValue = size
    this.element.querySelectorAll("[data-size]").forEach((item) => {
      const on = item.dataset.size === size
      item.setAttribute("aria-checked", String(on))
      const icon = item.querySelector("i")
      if (icon) icon.className = `bi ${on ? "bi-check2" : "bi-dot"}`
    })
    requestAnimationFrame(() => { this._applyPosition(); this._dodgeSidebar() })
    this._savePrefs({ size })
  }

  async useButton() {
    this.closeMenu()
    await this._savePrefs({ mode: "button" })
    // The header is server-rendered: reload it with the pill back in place.
    if (window.Turbo) window.Turbo.visit(window.location.href, { action: "replace" })
    else window.location.reload()
  }

  // "Hide" in the right-click menu: every screen hides (Jane for Mac, the
  // extension, other tabs). The header is server-rendered, so reload it with
  // the header button back in place - Jane stays one click away meanwhile.
  async hide(event) {
    const how = event.currentTarget.dataset.hide
    this.closeMenu()
    this.element.hidden = true
    await this._savePrefs({ hide: how })
    if (window.Turbo) window.Turbo.visit(window.location.href, { action: "replace" })
    else window.location.reload()
  }

  // Hidden or shown from another screen. Hidden: out of the way now, and the
  // next visit draws the header button instead. Shown again (or the time is
  // up while this page is open): back in the corner.
  _hiddenElsewhere(hiddenUntil) {
    clearTimeout(this._unhideTimer)
    if (!hiddenUntil) {
      this.element.hidden = false
      return
    }
    this.closeMenu()
    this.element.hidden = true
    this.element.removeAttribute("data-turbo-permanent")
    if (hiddenUntil === "forever") return
    const wait = new Date(hiddenUntil).getTime() - Date.now()
    if (wait > 0 && wait < 2 ** 31 - 1) this._unhideTimer = setTimeout(() => { this.element.hidden = false }, wait)
  }

  _savePrefs(body) {
    const token = document.querySelector("meta[name='csrf-token']")?.content
    return fetch(this.prefsUrlValue, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": token || "" },
      credentials: "same-origin",
      body: JSON.stringify(body)
    }).catch(() => null)
  }

  // --- moods ---------------------------------------------------------------

  _subscribe() {
    if (typeof createConsumer !== "function") return
    try {
      this._consumer = createConsumer()
      this._subscription = this._consumer.subscriptions.create(
        { channel: "AssistantActivityChannel" },
        { received: (data) => this._received(data), connected: () => this._announce() }
      )
    } catch (_error) {
      // The avatar still works without live moods.
    }
  }

  _inFront() {
    return document.visibilityState === "visible" && document.hasFocus()
  }

  _announce(front = this._inFront()) {
    const sidebar = this._sidebarElement()
    try {
      this._subscription?.perform("announce", {
        client: "web", id: this._clientId, focused: front, avatar: true,
        chat_open: Boolean(sidebar && this._visible(sidebar))
      })
    } catch (_error) {
      // A socket that is reconnecting; the next announce will do.
    }
  }

  _received(data) {
    if (data?.type === "huddle") {
      this._huddleReceive(data)
      return
    }
    // Someone switched who the avatar is (another tab, Jane for Mac): let the
    // next visit draw her fresh instead of carrying this one over.
    if (data?.type === "avatar_hidden") {
      this._hiddenElsewhere(data.hidden_until)
      return
    }
    if (data?.type === "avatar_persona") {
      if (Number(data.persona_id) !== this.personaIdValue) this.element.removeAttribute("data-turbo-permanent")
      return
    }
    if (!data || data.type !== "activity" || this._voiceActive) return
    // Only the one in the corner's own replies move them: a chat with your
    // twin doesn't animate Jane, and Jane's don't animate you.
    if (data.persona_id && this.personaIdValue && data.persona_id !== this.personaIdValue) return
    if (data.state === "thinking" || data.state === "talking") {
      this._setMood(data.state)
      return
    }
    this._setMood("idle")
    // A reply landed while the chat is closed: say so.
    const sidebar = this._sidebarElement()
    if (data.preview && !(sidebar && this._visible(sidebar))) this._showBubble(data.preview)
  }

  // --- Jane and your twin, on screen together (Assistant::Huddle) ---------

  // Back into the order they were said (seq), one at a time: the first
  // event of a huddle awaits the twin's face, and nothing may overtake it.
  _huddleReceive(data) {
    if (!data.seq) return this._huddleEnqueue(data)
    if (data.id !== this._orderId) {
      this._orderId = data.id
      this._nextSeq = 1
      this._pending = new Map()
      clearTimeout(this._gapTimer)
    }
    if (data.seq < this._nextSeq) return
    this._pending.set(data.seq, data)
    this._huddleDrain()
  }

  _huddleDrain() {
    clearTimeout(this._gapTimer)
    while (this._pending.has(this._nextSeq)) {
      const event = this._pending.get(this._nextSeq)
      this._pending.delete(this._nextSeq)
      this._nextSeq += 1
      this._huddleEnqueue(event)
    }
    if (this._pending.size) {
      this._gapTimer = setTimeout(() => {
        this._nextSeq = Math.min(...this._pending.keys())
        this._huddleDrain()
      }, HUDDLE_GAP_MS)
    }
  }

  _huddleEnqueue(event) {
    this._huddleChain = (this._huddleChain || Promise.resolve()).then(() => this._huddle(event)).catch(() => {})
  }

  async _huddle(data) {
    if (!this.hasHuddleTarget) return
    if (this._huddleId !== data.id) await this._huddleOpen(data.id)
    if (data.state === "line") {
      // Lines take the floor one at a time, so aloud they never talk over
      // each other and silent they still read as turns.
      this._huddleQueue.push(data)
      this._huddleNext()
    } else if (data.state === "status") {
      if (this._huddleSpeaking) return
      // A faint line on the busy one's side, until their words replace it.
      this._huddleLine(data.speaker, data.text, true)
      if (this._onMain(data.speaker)) this._setMood("thinking")
      else this._twinMood("thinking")
    } else if (data.state === "done") {
      this._huddleDone = true
      this._huddleMaybeClose()
    }
  }

  _huddleNext() {
    if (this._huddleSpeaking) return
    const line = this._huddleQueue.shift()
    if (!line) {
      this._huddleMaybeClose()
      return
    }
    this._huddleSpeaking = true
    const onMain = this._onMain(line.speaker)
    if (onMain) this._janeSay(line.speaker, line.text)
    else this._twinSay(line.speaker, line.text)
    const finished = () => {
      if (this._huddleAudio === audio) this._huddleAudio = null
      this._huddleSpeaking = false
      if (onMain) this._setMood("idle")
      else this._twinMood("idle")
      this._huddleNext()
    }
    let audio = null
    // Aloud only in a tab someone is looking at: a background tab stays quiet.
    if (this._huddleVoiceOn() && line.speech && document.visibilityState === "visible") {
      audio = new Audio(`/assistant/speak?huddle=${encodeURIComponent(line.speech)}`)
      this._huddleAudio = audio
      audio.onended = finished
      audio.onerror = () => setTimeout(finished, HUDDLE_READ_MS)
      audio.play().catch(() => setTimeout(finished, HUDDLE_READ_MS))
    } else {
      setTimeout(finished, HUDDLE_READ_MS)
    }
  }

  // Close once everything said has been heard, and a little after.
  _huddleMaybeClose() {
    if (!this._huddleDone || this._huddleSpeaking || this._huddleQueue.length) return
    clearTimeout(this._huddleTimer)
    this._huddleTimer = setTimeout(() => this._huddleClose(), HUDDLE_LINGER_MS)
  }

  _huddleVoiceOn() {
    try {
      return window.localStorage.getItem(HUDDLE_VOICE_KEY) === "on"
    } catch (_error) {
      return false
    }
  }

  toggleHuddleVoice(event) {
    event?.stopPropagation()
    const on = !this._huddleVoiceOn()
    try {
      window.localStorage.setItem(HUDDLE_VOICE_KEY, on ? "on" : "off")
    } catch (_error) {
      // Private mode: aloud for this page only.
    }
    if (!on && this._huddleAudio) {
      this._huddleAudio.pause()
      this._huddleAudio.onended?.()
    }
    this._renderHuddleVoice()
  }

  _renderHuddleVoice() {
    if (!this.hasHuddleVoiceTarget) return
    const on = this._huddleVoiceOn()
    this.huddleVoiceTarget.setAttribute("aria-pressed", String(on))
    this.huddleVoiceTarget.title = on ? "Stop saying it aloud" : "Say it aloud"
    this.huddleVoiceTarget.querySelector("i").className = `bi ${on ? "bi-volume-up-fill" : "bi-volume-mute"}`
  }

  async _huddleOpen(id) {
    this._huddleId = id
    this._huddleQueue = []
    this._huddleSpeaking = false
    this._huddleDone = false
    this._renderHuddleVoice()
    clearTimeout(this._huddleTimer)
    this._huddleTimer = setTimeout(() => this._huddleClose(), HUDDLE_MAX_MS)
    if (!this.twinShownValue && !this._me) await this._loadPeople()
    const me = this._companion() || { name: "You" }
    this.twinNameTarget.textContent = me.name
    this.twinTarget.setAttribute("aria-label", this.twinShownValue ? `Open ${me.name}` : `Talk to yourself: ${me.name}`)
    this.twinTarget.querySelectorAll("img, .jane-person-initials, .jane-avatar-canvas").forEach((node) => node.remove())
    this._twinFigure?.destroy()
    this.huddleTarget.classList.remove("jane-avatar-twin--figure")
    this._twinFigure = this._standUp(this.twinTarget, this.huddleTarget, me.cutouts)
    if (me.avatar) {
      const img = document.createElement("img")
      img.className = "jane-avatar-img"
      img.src = me.avatar
      img.alt = ""
      img.draggable = false
      this.twinTarget.prepend(img)
    } else {
      this.twinTarget.prepend(this._initials(me.name))
    }
    this.huddleLinesTarget.replaceChildren()
    this.huddleLinesTarget.hidden = false
    this._hideBubble()
    this.element.classList.toggle("is-huddle-right", this.element.getBoundingClientRect().left < HUDDLE_ROOM)
    this.element.classList.add("is-huddle")
    this.huddleTarget.hidden = false
    this._twinMood("idle")
  }

  _huddleClose() {
    clearTimeout(this._huddleTimer)
    this._huddleId = null
    this._huddleQueue = []
    this._huddleSpeaking = false
    if (this._huddleAudio) {
      this._huddleAudio.onended = null
      this._huddleAudio.pause()
      this._huddleAudio = null
    }
    this.element.classList.remove("is-huddle", "is-huddle-right")
    if (!this.hasHuddleTarget) return
    this.huddleTarget.hidden = true
    this.huddleLinesTarget.hidden = true
    this.huddleLinesTarget.replaceChildren()
    this._hideBubble()
    this._setMood("idle")
    if (this.hasTwinVideoTarget) {
      this.twinVideoTarget.pause()
      this.twinVideoTarget.hidden = true
      this._twinClip = null
    }
    this._twinFigure?.pause()
  }

  // One line in the conversation strip. A status ("… is thinking") is
  // replaced by whatever comes next; only the last few lines stay.
  _huddleLine(speaker, text, status = false) {
    if (!text) return
    const strip = this.huddleLinesTarget
    const last = strip.lastElementChild
    if (last?.classList.contains("is-status")) last.remove()
    const line = document.createElement("div")
    // The side is WHERE they stand (corner or beside it); the name is WHO.
    line.className = `jane-avatar-line jane-avatar-line--${this._onMain(speaker) ? "jane" : "twin"}`
    line.classList.toggle("is-status", status)
    const who = document.createElement("span")
    who.className = "jane-avatar-line-who"
    who.textContent = this._onMain(speaker) ? this._janeName() : (this._companion()?.name || "You")
    const words = document.createElement("span")
    words.className = "jane-avatar-line-text"
    words.textContent = text
    line.append(who, words)
    strip.appendChild(line)
    while (strip.children.length > HUDDLE_LINES) strip.firstElementChild.remove()
  }

  // The name of whoever is in the corner.
  _janeName() {
    return this.element.getAttribute("aria-label") || defaultAssistantName()
  }

  // A huddle line's speaker ("jane" / "twin") is on the corner avatar when
  // that is who's in the corner; otherwise on the one who stepped out.
  _onMain(speaker) {
    return (speaker === "twin") === this.twinShownValue
  }

  _companion() {
    return this.twinShownValue ? this.companionValue : (this._me || this.companionValue)
  }

  // Whoever has the floor talks; the other listens. _jane* is the corner
  // avatar and _twin* the one beside it, whoever they are.
  _janeSay(speaker, text) {
    this._huddleLine(speaker, text)
    this._setMood("talking")
    this._twinMood("listening")
  }

  _twinSay(speaker, text) {
    this._huddleLine(speaker, text)
    this._twinMood("talking")
    this._setMood("listening")
  }

  // The twin's living portrait, when it has one — the same moods as Jane's.
  _twinMood(mood) {
    this.twinTarget.dataset.mood = mood
    if (this._twinFigure) {
      const clips = this._twinFigure.clips
      const kind = this._chooseMood(mood, clips) || (this._twinFigure.url ? null : Object.keys(clips)[0])
      if (kind) this._twinFigure.play(clips[kind])
      return
    }
    if (this._portrait || !this.hasTwinVideoTarget || this._reducedMotion) return
    const clips = this._companion()?.clips || {}
    const kind = this._chooseMood(mood, clips)
    if (!kind) return
    const video = this.twinVideoTarget
    if (this._twinClip === kind && !video.paused) return
    this._twinClip = kind
    video.loop = true
    video.src = clips[kind]
    video.onplaying = () => { video.hidden = false }
    video.play().catch(() => { video.hidden = true })
  }

  // "Talk it over with …" in the right-click menu: the topic box, then off
  // they go — the huddle arrives over the live channel.
  talkItOver(event) {
    event.stopPropagation()
    if (!this.hasTalkTarget) return
    if (!this.menuTarget.classList.contains("is-open")) this.openMenu(event)
    this._showMenuView("settings")
    this.talkTarget.hidden = false
    this._fitMenu()
    this.talkTopicTarget.focus()
  }

  async submitTalk(event) {
    event.preventDefault()
    const topic = this.talkTopicTarget.value.trim()
    if (!topic) return
    const token = document.querySelector("meta[name='csrf-token']")?.content
    try {
      const response = await fetch(this.talkTarget.action, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": token || "" },
        credentials: "same-origin",
        body: JSON.stringify({ topic })
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        this._showBubble(data.error || "Couldn't start that.")
        return
      }
      this.talkTopicTarget.value = ""
      this.talkTarget.hidden = true
      this.closeMenu()
    } catch (_error) {
      this._showBubble("Couldn't start that.")
    }
  }

  // Your twin's chat, in the Messages panel.
  openTwin() {
    const url = this._me?.thread_url || this.meThreadUrlValue
    if (!url) return
    const name = this.twinShownValue ? this.element.getAttribute("aria-label") : this._me?.name
    window.dispatchEvent(new CustomEvent("jane:compose", { detail: { url, title: name, avatar: this._me?.avatar } }))
  }

  // The one beside the corner in a huddle: Jane's chat, or your twin's.
  openCompanion() {
    if (this.twinShownValue) this._openJane()
    else this.openTwin()
  }

  _voice(state) {
    const moods = { listening: "listening", speaking: "talking", thinking: "thinking" }
    this._voiceActive = Boolean(moods[state]) || state === "connecting"
    this._setMood(moods[state] || "idle")
  }

  _setMood(mood) {
    this.element.dataset.mood = mood
    this._playClip(mood)
  }

  _chooseMood(mood, clips) {
    const kinds = (MOOD_CLIPS[mood] || []).filter((kind) => clips[kind])
    return kinds[Math.floor(Math.random() * kinds.length)]
  }

  _playClip(mood) {
    if (this._figure) {
      const kind = this._chooseMood(mood, this._figure.clips) || (this._figure.url ? null : Object.keys(this._figure.clips)[0])
      if (kind) this._figure.play(this._figure.clips[kind])
      return
    }
    if (this._portrait || !this.hasVideoTarget || this._reducedMotion) return
    const clips = this.clipsValue || {}
    const kind = this._chooseMood(mood, clips)
    if (!kind) return
    const video = this.videoTarget
    if (this._clip === kind && !video.paused) return
    this._clip = kind
    video.loop = true
    video.src = clips[kind]
    video.onplaying = () => { video.hidden = false }
    video.play().catch(() => { video.hidden = true })
  }

  _showBubble(text) {
    if (!this.hasBubbleTarget) return
    this.bubbleTarget.classList.remove("is-status")
    this.bubbleTextTarget.textContent = plainText(text)
    this.bubbleTarget.hidden = false
    this.element.classList.add("is-nodding")
    setTimeout(() => this.element.classList.remove("is-nodding"), 900)
    clearTimeout(this._bubbleTimer)
    this._bubbleTimer = setTimeout(() => this._hideBubble(), BUBBLE_MS)
  }

  _hideBubble() {
    if (this.hasBubbleTarget) this.bubbleTarget.hidden = true
  }
}
